import { useEffect, useRef } from 'react'
import { XanoClient } from '@xano/js-sdk'

/**
 * Xano Realtime (WebSocket) client + a thin React hook.
 *
 * These two values are PUBLIC client identifiers (safe to ship in the browser —
 * that's the whole point of the connection canonical + anonymous channels). No
 * Xano auth token is exposed here: our realtime channels have "Anonymous
 * Clients" enabled, so the canonical alone is enough to subscribe.
 */
const INSTANCE_BASE_URL = 'https://xltw-mzzg-kcjz.s2.xano.io'
const REALTIME_CANONICAL = '0-fdQ6AVX9nCnkEeGbgwC4nL15U'

let _client = null
export function xanoRealtimeClient() {
  if (!_client) {
    _client = new XanoClient({
      instanceBaseUrl: INSTANCE_BASE_URL,
      realtimeConnectionCanonical: REALTIME_CANONICAL,
    })
  }
  return _client
}

/*
 * ONE SDK channel per name, shared by every component subscribed to it.
 *
 * 🚨 Why: `client.channel(name)` returns a NEW object on every call, but they all
 * ride ONE WebSocket, and `destroy()` sends a channel-wide `Leave` for that socket
 * (read from @xano/js-sdk 3.0.1). With two subscribers on one name, the first to
 * unmount unsubscribed the tab for BOTH — no error, the events just stopped. The
 * live case: the sidebar badge (Layout) and /calls both listen on `call_update`,
 * so leaving /calls killed the badge's live count until a reload.
 *
 * So the first subscriber opens the channel, later ones join its fan-out, and it
 * is destroyed only when the LAST one leaves — after a short grace, so a route
 * change between two pages on the same channel (or StrictMode's mount → unmount →
 * mount) reuses it instead of sending Leave and then Join.
 */
const CLOSE_GRACE_MS = 1000

/*
 * 🚨 THE JOIN RACE — why attaching is retried, and why this is not optional.
 *
 * `client.channel(name)` SENDS ITS JOIN FROM THE CONSTRUCTOR, and decides it may
 * send by testing that a socket OBJECT exists (@xano/js-sdk 3.0.1,
 * models/realtime-channel.js): `getSocket() === null` → return, otherwise
 * synthesise a `status: Connected` action and `socket.send(Join)`.
 *
 * "A socket exists" is not "the socket is open". For the first few hundred ms the
 * socket is CONNECTING, and `send()` on a CONNECTING socket throws
 * `InvalidStateError: Sent before connected` — per the WebSocket spec, in browsers
 * exactly as in node. So:
 *
 *   the FIRST channel of the page      → getSocket() is null → no send → joined
 *                                        later, when the socket opens. Works.
 *   every channel opened AFTER that,
 *   until the socket finishes opening  → throws.
 *
 * A patient page mounts PatientLayout, the sidebar and the chat dock in the same
 * tick; /appointments mounts the calendar's two. One channel survived per page
 * load and the rest died — silently, because useXanoRealtime caught the throw,
 * logged a warning and never tried again. Symptom (reported 2026-09-13): patient
 * status changes correctly in Xano, every trigger fires, an external subscriber
 * receives the events — and no browser screen moves until a reload.
 *
 * So the entry is created immediately and the SDK channel is attached when it can
 * actually be attached. Subscribers register against the entry, not the channel,
 * so they miss nothing once the join lands: the events they care about come from
 * a human action seconds later, not inside the connect window.
 */
const ATTACH_RETRY_MS = 100    // first retry; doubles
const ATTACH_RETRY_CAP_MS = 8000
const _channels = new Map() // name → { name, ch, subs: Set<callback ref>, closeTimer, retryTimer, closed }

function attach(entry, delay = ATTACH_RETRY_MS) {
  entry.retryTimer = null
  if (entry.closed || entry.ch) return
  const { name } = entry
  let ch
  try {
    ch = xanoRealtimeClient().channel(name)
  } catch (e) {
    // The join race above — a timing problem, not a failure. Back off and retry.
    if (delay > ATTACH_RETRY_CAP_MS) {
      console.warn('[realtime]', name, 'gave up attaching', e)
      return
    }
    entry.retryTimer = setTimeout(() => attach(entry, delay * 2), delay)
    return
  }
  try {
    ch.on((msg) => {
      // Backend broadcasts (`api.realtime_event`) arrive as action "event"; client
      // "message" sends are passed through for safety. Everything else (connection
      // status, presence) is the SDK's business.
      if (!msg || (msg.action !== 'event' && msg.action !== 'message')) return
      // Copy first: a handler can unmount a subscriber mid-loop. One throwing
      // handler must not starve the others.
      for (const ref of [...entry.subs]) {
        try { ref.current?.(msg) } catch (e) { console.warn('[realtime]', name, 'handler threw', e) }
      }
    }, (err) => console.warn('[realtime]', name, 'error', err))
  } catch (e) {
    try { ch.destroy() } catch { /* already gone */ }
    console.warn('[realtime]', name, 'could not attach handler', e)
    return
  }
  entry.ch = ch
}

function openShared(name) {
  const existing = _channels.get(name)
  if (existing) {
    clearTimeout(existing.closeTimer)
    existing.closeTimer = null
    existing.closed = false
    // A name re-subscribed inside the close grace may have lost its retry to the
    // cleanup below; put it back on the road.
    if (!existing.ch && !existing.retryTimer) attach(existing)
    return existing
  }
  const entry = { name, ch: null, subs: new Set(), closeTimer: null, retryTimer: null, closed: false }
  _channels.set(name, entry)
  attach(entry)
  return entry
}

function closeShared(name, ref) {
  const entry = _channels.get(name)
  if (!entry) return
  entry.subs.delete(ref)
  if (entry.subs.size > 0 || entry.closeTimer) return
  entry.closeTimer = setTimeout(() => {
    entry.closeTimer = null
    if (entry.subs.size > 0) return // someone re-subscribed during the grace
    _channels.delete(name)
    entry.closed = true
    clearTimeout(entry.retryTimer)
    entry.retryTimer = null
    try { entry.ch?.destroy() } catch { /* already gone */ }
  }, CLOSE_GRACE_MS)
}

/**
 * Subscribe to a Xano realtime channel while mounted. Calls `onEvent(msg)` for
 * backend broadcasts. `msg` = { action, payload }.
 *
 * Pass a falsy `channelName` to subscribe to nothing (e.g. before a branch is
 * chosen). The subscription follows `channelName`; `onEvent` is held in a ref so
 * changing the callback doesn't resubscribe. Any number of components may
 * subscribe to the same name — they share one channel (see above).
 */
export function useXanoRealtime(channelName, onEvent) {
  const cbRef = useRef(onEvent)
  cbRef.current = onEvent

  useEffect(() => {
    if (!channelName) return
    // Training sandbox: realtime channels are shared with the live clinic (Xano
    // channels are not data-source-scoped), so the sandbox neither listens nor —
    // server-side — rings. Screens still refetch after the trainee's own actions.
    if (document.documentElement.dataset.training === '1') return
    let entry
    try {
      entry = openShared(channelName)
    } catch (e) {
      console.warn('[realtime] subscribe failed for', channelName, e)
      return
    }
    entry.subs.add(cbRef)
    return () => closeShared(channelName, cbRef)
  }, [channelName])
}
